import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/pages/Home";
import useSEO from "@/hooks/useSEO";
import { PORTFOLIO } from "@/data/content";

const PortfolioCase = () => {
  const { id } = useParams();
  const index = PORTFOLIO.findIndex((p) => p.id === id);
  const item = PORTFOLIO[index];

  useSEO(
    item ? `${item.title} — SSDE LTD Portfolio Concept` : "Portfolio — SSDE LTD",
    item ? item.description : "SSDE LTD portfolio of design concepts."
  );

  if (!item) {
    return (
      <section className="bg-offwhite min-h-[70vh] flex items-center pt-24" data-testid="case-not-found">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center w-full">
          <h1 className="font-serif text-4xl tracking-tight text-charcoal">Project not found.</h1>
          <Link to="/portfolio" className="btn-gold mt-8" data-testid="case-back-button">
            <ArrowLeft size={15} strokeWidth={1.8} />
            Back to Portfolio
          </Link>
        </div>
      </section>
    );
  }

  const prev = PORTFOLIO[(index - 1 + PORTFOLIO.length) % PORTFOLIO.length];
  const next = PORTFOLIO[(index + 1) % PORTFOLIO.length];

  return (
    <>
      <PageHero
        testId="case-hero"
        eyebrow={`${item.category} · Concept Project`}
        titleLines={[item.title]}
        lede={item.description}
      />

      <section className="bg-white py-16 sm:py-24" data-testid="case-content">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <figure className="border border-line bg-offwhite">
              <img
                src={item.image}
                alt={`${item.title} — ${item.category} concept design by SSDE LTD`}
                className="w-full aspect-[16/8] object-cover"
                data-testid="case-hero-image"
              />
            </figure>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-7">
              <p className="eyebrow mb-5">Overview</p>
              <p className="text-base sm:text-lg text-charcoal leading-relaxed">{item.overview}</p>
              <p className="mt-6 text-base text-charcoal-mute leading-relaxed">{item.approach}</p>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="border border-line bg-offwhite p-8">
                <p className="eyebrow mb-6">Deliverables</p>
                <ul className="space-y-4">
                  {item.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm text-charcoal">
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 border border-gold/50 bg-white text-gold-deep shrink-0">
                        <Check size={12} strokeWidth={2.2} />
                      </span>
                      {d}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-line flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">
                  <span>{item.category}</span>
                  <span className="text-gold-deep">Concept</span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16">
            <div className="border border-line bg-ivory px-8 py-8 sm:px-12 text-center">
              <p className="font-serif text-xl sm:text-2xl text-charcoal leading-snug max-w-2xl mx-auto">
                "This is an illustrative concept study. It demonstrates the standard
                of thinking and craft we bring to every commission."
              </p>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line">
            <Link
              to={`/portfolio/${prev.id}`}
              data-testid="case-prev-link"
              className="group bg-white p-7 transition-colors duration-300 hover:bg-offwhite"
            >
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">
                <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
                Previous Project
              </span>
              <span className="block mt-3 font-serif text-xl text-charcoal group-hover:text-gold-deep transition-colors duration-300">
                {prev.title}
              </span>
            </Link>
            <Link
              to={`/portfolio/${next.id}`}
              data-testid="case-next-link"
              className="group bg-white p-7 text-right transition-colors duration-300 hover:bg-offwhite"
            >
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">
                Next Project
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <span className="block mt-3 font-serif text-xl text-charcoal group-hover:text-gold-deep transition-colors duration-300">
                {next.title}
              </span>
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default PortfolioCase;
